import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

interface FAQ {
  question: string;
  answer: string;
}

interface ToolPageLayoutProps {
  title: string;
  description: string;
  h1: string;
  category: string;
  categoryPath: string;
  howToUse: string[];
  faq: FAQ[];
  children: React.ReactNode;
}

export const ToolPageLayout = ({
  title,
  description,
  h1,
  category,
  categoryPath,
  howToUse,
  faq,
  children,
}: ToolPageLayoutProps) => {
  return (
    <div className="max-w-4xl mx-auto px-2 md:p-8">
      <Helmet>
        <title>{title} – 247 DevTools Hub</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={`${title} – 247 DevTools Hub`} />
        <meta property="og:description" content={description} />
      </Helmet>

      {/* Breadcrumbs */}
      <nav className="text-sm mb-4 text-slate-500">
        <Link to="/" className="hover:text-blue-600">Home</Link> / 
        <Link to={categoryPath} className="hover:text-blue-600"> {category}</Link> / 
        <span className="font-semibold text-slate-900"> {h1}</span>
      </nav>

      <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">{h1}</h1>
      <p className="text-lg text-slate-600 mb-8">{description}</p>

      {/* Trust Section */}
      <div className="bg-blue-50 p-4 rounded-lg mb-8 text-sm text-blue-800">
        <strong>Privacy First:</strong> All processing is done entirely in your browser. Your data never leaves your device. No sign-up required.
      </div>

      {/* The Tool */}
      <div className="mb-12">
        {children}
      </div>

      {/* How to use */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-slate-900 mb-4">How to use this tool</h2>
        <ol className="list-decimal list-inside space-y-2 text-slate-700">
          {howToUse.map((step, i) => <li key={i}>{step}</li>)}
        </ol>
      </section>

      {/* FAQ */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-slate-900 mb-4">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {faq.map((item, i) => (
            <div key={i}>
              <h3 className="font-semibold text-slate-900">{item.question}</h3>
              <p className="text-slate-700">{item.answer}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
